import type * as React from "react";
import { Box, Link, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Link as RouterLink } from "react-router-dom";
import { PATHS } from "constants/pathConstants";
import { COLORS } from "components/v3/Theme/colors";
import { INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED } from "components/v3/pages/account/PlanInformationPage/InactivePlanCard";
import {
  getNoticeAlertContainerSx,
  noticeAlertIconSx
} from "components/v3/sharedComponents/noticeAlertStyles";

const mailtoNavigation = "mailto:navigation@genomiclife.com";

const legacyMailLinkSx = {
  color: COLORS.WHITE,
  fontWeight: 600,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": {
    color: COLORS.WHITE
  }
} as const;

const LegacyInactiveMembershipExpiredBanner: React.FC = () => {
  return (
    <Box
      sx={{
        ...getNoticeAlertContainerSx(COLORS.NOTICE),
        alignItems: "center",
        flexDirection: "column",
        gap: 1.5,
        justifyContent: "center",
        py: 2,
        px: 2,
        textAlign: "center",
        width: "100%"
      }}
      role="alert"
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 1.5,
          justifyContent: "center",
          width: "100%"
        }}
      >
        <WarningAmberRoundedIcon sx={noticeAlertIconSx} aria-hidden />
        <Typography
          component="p"
          variant="body_strong_lg"
          sx={{
            color: COLORS.WHITE,
            fontSize: { xs: "1.125rem", md: "1.25rem" },
            fontWeight: 700,
            lineHeight: 1.35,
            margin: 0,
            textAlign: "center"
          }}
        >
          Your Genomic Life membership is inactive
        </Typography>
      </Box>
      <Typography
        component="p"
        variant="body2"
        sx={{
          color: COLORS.WHITE,
          fontWeight: 400,
          lineHeight: 1.5,
          margin: 0,
          maxWidth: "min(40rem, 100%)",
          mx: "auto",
          opacity: 0.95,
          textAlign: "center"
        }}
      >
        You are unable to order a kit or report a cancer diagnosis to obtain
        oncology support. Please contact us at{" "}
        <Link href={mailtoNavigation} sx={legacyMailLinkSx}>
          navigation@genomiclife.com
        </Link>
        .
      </Typography>
    </Box>
  );
};

const ReactivationMembershipExpiredBanner: React.FC = () => {
  return (
    <Box
      aria-label="Your membership has expired. Go to plan information to reactivate."
      component={RouterLink}
      to={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION}
      sx={{
        ...getNoticeAlertContainerSx(COLORS.NOTICE),
        alignItems: "center",
        color: COLORS.WHITE,
        cursor: "pointer",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: { xs: "flex-start", md: "center" },
        textDecoration: "none",
        transition: "filter 0.2s ease",
        width: "100%",
        "&:hover": {
          color: COLORS.WHITE,
          filter: "brightness(0.95)",
          textDecoration: "none"
        },
        "&:focus-visible": {
          outline: `2px solid ${COLORS.WHITE}`,
          outlineOffset: 2
        }
      }}
    >
      <WarningAmberRoundedIcon sx={noticeAlertIconSx} aria-hidden />
      <Typography
        component="span"
        variant="body_strong"
        sx={{
          color: COLORS.WHITE,
          flex: { xs: "1 1 auto", md: "0 1 auto" },
          minWidth: 0,
          textAlign: { xs: "left", md: "center" }
        }}
      >
        Your membership has expired. Click{" "}
        <Box
          component="span"
          sx={{ fontWeight: 600, textDecoration: "underline" }}
        >
          here
        </Box>{" "}
        to reactivate.
      </Typography>
    </Box>
  );
};

const MembershipExpiredBanner: React.FC = () =>
  INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED ? (
    <LegacyInactiveMembershipExpiredBanner />
  ) : (
    <ReactivationMembershipExpiredBanner />
  );

export default MembershipExpiredBanner;
